import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import type { ItemReference } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { directoryKeys } from '@/utils/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { getParentPath, isAncestorOrSelf } from '../utils';
import type { FileManagerMountsState } from '../logic/core/useFileManagerMounts';
import type { FileManagerNavigationState } from '../logic/core/useFileManagerNavigation';
import type { FileSystemItem } from '../types';

export type MoveItemVariables = {
  item: FileSystemItem;
  destinationParentPath: string;
  /**
   * Pre-resolved destination reference, e.g. from a materialized drop-target item's own
   * id/driveId. When present, skips the getItemReference() round-trip.
   */
  destinationParent?: ItemReference;
};

// Takes navigation/mounts explicitly rather than self-injecting via useFileManagerNavigation()/
// useFileManagerMounts(): this mutation is constructed from provideFileManagerTransfer(), called
// directly in FileManager.vue's own setup() — the same instance that provides navigation/mounts,
// which a component can never inject its own provide() call from.
export const useMoveItemMutation = (
  navigation: Pick<FileManagerNavigationState, 'currentPath' | 'navigateTo'>,
  mounts: Pick<FileManagerMountsState, 'resolve'>,
) => {
  const queryClient = useQueryClient();
  const { currentPath, navigateTo } = navigation;
  const { resolve } = mounts;

  return useMutation({
    mutationKey: ['directoryService', 'moveItem'],
    mutationFn: async ({ item, destinationParentPath, destinationParent }: MoveItemVariables) => {
      let destParent = destinationParent;
      if (!destParent) {
        // Only the destination needs resolving: the source is addressed purely by id off its own
        // parentReference. A drop that crosses the mount boundary never reaches this mutation at all
        // — the transfer policy resolves it to a copy, because Graph cannot PATCH an item between
        // drives.
        const target = resolve(destinationParentPath);
        if (!target) {
          throw new Error(`Cannot move into ${destinationParentPath}: no accessible library`);
        }
        destParent = await sharePointDriveService.getItemReference(target);
      }
      // The third argument stays the VIRTUAL destination path — it is what the returned item's path
      // is built from, and everything above this mutation navigates by virtual paths.
      return sharePointDriveService.moveItem(item, destParent, destinationParentPath);
    },
    onSuccess: (movedItem, { item, destinationParentPath }) => {
      const sourceParentPath = getParentPath(item.path);
      queryClient.invalidateQueries({
        queryKey: directoryKeys.children(sourceParentPath),
      });
      queryClient.invalidateQueries({
        queryKey: directoryKeys.children(destinationParentPath),
      });

      // A moved directory can flip its old/new parent's `hasFolderChildren` flag
      // (e.g. removing the last subfolder), and that flag lives on the
      // parent's own entry in the *grandparent's* children list.
      if (item.type === 'directory') {
        queryClient.invalidateQueries({
          queryKey: directoryKeys.children(getParentPath(sourceParentPath)),
        });
        queryClient.invalidateQueries({
          queryKey: directoryKeys.children(
            getParentPath(destinationParentPath),
          ),
        });
      }

      if (
        item.type === 'directory' &&
        isAncestorOrSelf(item.path, currentPath.value)
      ) {
        navigateTo(movedItem.path + currentPath.value.slice(item.path.length), { replace: true });
      }
    },
  });
};
